// LeadersPage — rebuilt per docs/FIX-LEADERS-DASHBOARD.md as a dashboard:
// Needs attention, This week's stats, Preaching insights, and a Manage grid.
// Reuses every existing admin/moderator tool (ModerationSheet, plan/access
// lists, InvitePanel, church settings, event editor, the Bible Map's
// Insights tab) rather than rebuilding them — this page just regroups entry
// points into the new layout.
//
// Props:
//   churchId, churchSlug, churchName
//   churches, sermonNoteChurchIds       — for the Preaching insights card's
//                                          Bible Map overlay (own church only)
//   user, isModerator
//   pendingAccess, accessActionState, onApproveAccess, onDeclineAccess
//   onOpenSermon(sermon), onOpenReports(), onOpenRemoved()
//   onOpenAddSermons(), onOpenChurchSettings()
//   onAddEvent()            — opens EventEditorSheet, blank
//   onClose()

import { useEffect, useState } from "react";
import QRCode from "qrcode";
import { supabase } from "../supabase";
import { InvitePanel } from "./Auth";
import { useNotices } from "../hooks/useNotices";
import { BibleMapView } from "./ExploreTab";
import ChurchSwitcherSheet, { ChurchSwitcherPill, ViewingAsStrip } from "./ChurchSwitcherSheet";

const SANS = "'Plus Jakarta Sans', system-ui, sans-serif";
const GOLD = "var(--gold)";

function startOfWeekISO() {
  const d = new Date(); d.setHours(0, 0, 0, 0);
  const day = d.getDay(); // 0=Sun..6=Sat
  const diff = day === 0 ? 6 : day - 1; // Monday start
  d.setDate(d.getDate() - diff);
  return d.toISOString();
}

export default function LeadersPage({
  churchId,
  churchSlug,
  churchName,
  churches = [],
  sermonNoteChurchIds = [],
  user,
  isModerator,
  pendingAccess = [],
  accessActionState = {},
  onApproveAccess,
  onDeclineAccess,
  onOpenSermon,
  onOpenReports,
  onOpenRemoved,
  onOpenAddSermons,
  onOpenChurchSettings,
  onAddEvent,
  onClose,
  // Super-admin "view the app as" switcher (docs/FIX-PLANS.md §1e §B2) —
  // isSuperAdmin/activeChurchId/homeChurchId undefined for everyone else,
  // so the pill/strip below simply don't render.
  isSuperAdmin,
  activeChurchId,
  homeChurchId,
  onSwitchChurch,
}) {
  const [view, setView] = useState("dashboard"); // dashboard | plans | access | invites | notices
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [insightsOpen, setInsightsOpen] = useState(false);
  const [reportsCount, setReportsCount] = useState(0);
  const [plansToApprove, setPlansToApprove] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [sundayPlanRow, setSundayPlanRow] = useState(null); // this week's own sermon+plan status, for the Needs-attention row
  const [leaderPrayers, setLeaderPrayers] = useState([]);
  const [weekStats, setWeekStats] = useState({ active: 0, devoPct: 0, praying: 0 });

  useEffect(() => {
    if (!churchId) { setReportsCount(0); return; }
    (async () => {
      const { count } = await supabase.from("reports")
        .select("id", { count: "exact", head: true })
        .eq("church_id", churchId)
        .eq("resolved", false);
      setReportsCount(count || 0);
    })();
  }, [churchId]);

  useEffect(() => {
    if (!churchId) { setPlansToApprove([]); setPlansLoading(false); return; }
    setPlansLoading(true);
    (async () => {
      const { data, error } = await supabase
        .from("reading_plans")
        .select("id, status, sermons(id, title, series, preacher, date, youtube_id, youtube_url, duration, church_id)")
        .eq("church_id", churchId)
        .in("status", ["draft", "approved"])
        .order("updated_at", { ascending: false });
      if (error) { console.warn("Load plans to approve failed:", error.message); setPlansToApprove([]); }
      else setPlansToApprove(data || []);
      setPlansLoading(false);
    })();
  }, [churchId]);

  // "Sunday's sermon has no devotional yet" row — the latest sermon with no
  // reading plan at all yet (mirrors ThisWeekTab's own no-devotional check).
  useEffect(() => {
    if (!churchId) { setSundayPlanRow(null); return; }
    (async () => {
      const { data: latest } = await supabase.from("sermons")
        .select("id, title, display_title, date").eq("church_id", churchId)
        .order("date", { ascending: false }).limit(1).maybeSingle();
      if (!latest) { setSundayPlanRow(null); return; }
      const { count } = await supabase.from("reading_plans")
        .select("id", { count: "exact", head: true }).eq("sermon_id", latest.id);
      setSundayPlanRow({ sermon: latest, hasPlan: (count || 0) > 0 });
    })();
  }, [churchId]);

  // Prayer requests shared with leaders only (docs/FIX-LEADERS-DASHBOARD.md) —
  // reuses prayers.visibility='elders' + its composer UI, already built in
  // CommunityTab.jsx/supabase-schema-prayer-visibility.sql earlier this session.
  useEffect(() => {
    if (!churchId) { setLeaderPrayers([]); return; }
    (async () => {
      const { data, error } = await supabase.from("prayers")
        .select("id, text, author_id, created_at, profiles(full_name)")
        .eq("church_id", churchId).eq("visibility", "elders")
        .order("created_at", { ascending: false });
      if (error) { setLeaderPrayers([]); return; }
      setLeaderPrayers(data || []);
    })();
  }, [churchId]);

  // This week at [church] — aggregate numbers only, never named lists.
  // "Members active" approximates app activity from the two things already
  // tracked (devotional opens, prayer reactions) — there's no dedicated
  // last-active/session tracking in this app yet.
  useEffect(() => {
    if (!churchId) { setWeekStats({ active: 0, devoPct: 0, praying: 0 }); return; }
    (async () => {
      const since = startOfWeekISO();
      const [membersRes, followsRes, prayedRes] = await Promise.all([
        supabase.from("profiles").select("id", { count: "exact", head: true }).eq("church_id", churchId),
        supabase.from("reading_plan_follows").select("user_id, days_completed, updated_at, reading_plans!inner(church_id)")
          .eq("reading_plans.church_id", churchId).gte("updated_at", since),
        supabase.from("prayer_reactions").select("user_id, created_at, prayers!inner(church_id)")
          .eq("prayers.church_id", churchId).gte("created_at", since),
      ]);
      const totalMembers = membersRes.count || 0;
      const follows = followsRes.data || [];
      const pray = prayedRes.data || [];
      const activeUsers = new Set([...follows.map(f => f.user_id), ...pray.map(p => p.user_id)]);
      const didDevo = follows.filter(f => (f.days_completed || 0) > 0).length;
      setWeekStats({
        active: activeUsers.size,
        devoPct: totalMembers ? Math.round((didDevo / totalMembers) * 100) : 0,
        praying: new Set(pray.map(p => p.user_id)).size,
      });
    })();
  }, [churchId]);

  const accessCount = pendingAccess.length;

  const joinUrl = churchSlug ? `https://thebiblecommunity.co.uk/join/${churchSlug}` : "";
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [linkCopied, setLinkCopied] = useState(false);
  useEffect(() => {
    if (!joinUrl) { setQrDataUrl(""); return; }
    QRCode.toDataURL(joinUrl, { width: 160, margin: 1 })
      .then(setQrDataUrl)
      .catch(() => setQrDataUrl(""));
  }, [joinUrl]);
  async function copyJoinLink() {
    try { await navigator.clipboard.writeText(joinUrl); setLinkCopied(true); setTimeout(() => setLinkCopied(false), 2000); }
    catch { /* clipboard unavailable — link is still shown as text */ }
  }

  const needsAttention = [
    sundayPlanRow && !sundayPlanRow.hasPlan && {
      key: "devo", icon: "📖", bg: "var(--peach)",
      title: `${sundayPlanRow.sermon.display_title || sundayPlanRow.sermon.title || "Sunday's sermon"} · not created yet`,
      action: "Create it", onClick: () => onOpenSermon?.(sundayPlanRow.sermon),
    },
    plansToApprove.length > 0 && {
      key: "plans", icon: "✓", bg: "var(--sage)", count: plansToApprove.length,
      title: `${plansToApprove.length} devotional plan${plansToApprove.length === 1 ? "" : "s"} ready to approve`,
      action: "Review", onClick: () => setView("plans"),
    },
    accessCount > 0 && {
      key: "access", icon: "👤", bg: "var(--sage)", count: accessCount,
      title: `${accessCount} ${accessCount === 1 ? "person" : "people"} asking to join`,
      action: "Review", onClick: () => setView("access"),
    },
    leaderPrayers.length > 0 && {
      key: "prayers", icon: "🙏", bg: "var(--blush)", count: leaderPrayers.length,
      title: `${leaderPrayers.length} new · shared with leaders only`,
      action: "View", onClick: () => setView("leaderPrayers"),
    },
    reportsCount > 0 && {
      key: "reports", icon: "⚑", bg: "#EDE8E0", count: reportsCount,
      title: `${reportsCount} post${reportsCount === 1 ? "" : "s"} flagged by members`,
      action: "Review", onClick: onOpenReports,
    },
  ].filter(Boolean);

  const manageTiles = [
    { key: "sermons", label: "Sermons", icon: "🎬", onClick: onOpenAddSermons },
    { key: "events", label: "Events", icon: "📅", onClick: onAddEvent },
    { key: "notices", label: "Notices", icon: "📌", onClick: () => setView("notices") },
    { key: "team", label: "Team & roles", icon: "👥", onClick: () => setView("invites") },
    { key: "invite", label: "Invite & QR", icon: "🔗", onClick: () => setView("invites") },
    { key: "settings", label: "Settings", icon: "⚙️", onClick: onOpenChurchSettings },
  ];

  return (
    <div style={S.overlay}>
      <div style={S.header}>
        {view === "dashboard" ? (
          <>
            <button onClick={onClose} style={S.backCircle} aria-label="Close">←</button>
            <div style={{ flex: 1, minWidth: 0 }}>
              {isSuperAdmin ? (
                <ChurchSwitcherPill church={churches.find(c => c.id === activeChurchId)} onClick={() => setSwitcherOpen(true)}/>
              ) : (
                <div style={S.churchName}>{churchName || ""}</div>
              )}
              <div style={S.pageTitle}>Leaders</div>
            </div>
          </>
        ) : (
          <>
            <button onClick={() => setView("dashboard")} style={S.backBtn}>← Back</button>
            <button onClick={onClose} style={S.closeBtn}>✕</button>
          </>
        )}
      </div>

      {isSuperAdmin && activeChurchId && homeChurchId && activeChurchId !== homeChurchId && (
        <ViewingAsStrip
          churchName={churches.find(c => c.id === activeChurchId)?.name || "another church"}
          homeName={churches.find(c => c.id === homeChurchId)?.name || "home"}
          onBack={() => onSwitchChurch?.(homeChurchId)}
        />
      )}
      {switcherOpen && (
        <ChurchSwitcherSheet
          churches={churches}
          activeChurchId={activeChurchId}
          homeChurchId={homeChurchId}
          onSelect={(id) => { onSwitchChurch?.(id); setSwitcherOpen(false); }}
          onClose={() => setSwitcherOpen(false)}
        />
      )}

      <div style={{ flex: 1, overflowY: "auto" }}>
        {view === "dashboard" && (
          <div style={S.wrap}>
            {/* Needs attention */}
            <div style={S.card}>
              <div style={S.cardHeadRow}>
                <div style={S.cardLabel}>NEEDS ATTENTION</div>
                {needsAttention.length > 0 && <div style={{ fontSize: 12, color: "var(--stone)" }}>{needsAttention.length} things</div>}
              </div>
              {needsAttention.length === 0 ? (
                <div style={S.caughtUp}>You're all caught up ✓</div>
              ) : needsAttention.map(r => (
                <button key={r.key} style={S.attentionRow} onClick={r.onClick}>
                  <span style={{ ...S.attentionIcon, background: r.bg }}>{r.icon}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={S.rowLabel}>{r.title}</div>
                  </div>
                  {r.action === "Create it" ? (
                    <span style={S.createItBtn}>{r.action}</span>
                  ) : (
                    <span style={S.reviewLink}>{r.action}</span>
                  )}
                </button>
              ))}
            </div>

            {/* This week */}
            <div style={S.card}>
              <div style={S.cardLabel}>THIS WEEK AT {(churchName || "").toUpperCase()}</div>
              <div style={S.statsRow}>
                <div style={S.statCol}><div style={S.statValue}>{weekStats.active}</div><div style={S.statLabel}>Members active</div></div>
                <div style={S.statCol}><div style={S.statValue}>{weekStats.devoPct}%</div><div style={S.statLabel}>Did the devotional</div></div>
                <div style={S.statCol}><div style={S.statValue}>{weekStats.praying}</div><div style={S.statLabel}>Praying</div></div>
              </div>
            </div>

            {/* Preaching insights */}
            <button type="button" style={S.insightsCard} onClick={() => setInsightsOpen(true)}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={S.rowLabel}>Preaching insights</div>
                <div style={S.rowSub}>What your church has covered in the Bible</div>
              </div>
              <div style={S.chevron}>›</div>
            </button>

            {/* Manage */}
            <div style={S.cardLabel}>MANAGE</div>
            <div style={S.manageGrid}>
              {manageTiles.map(t => (
                <button key={t.key} style={S.manageTile} onClick={t.onClick}>
                  <span style={S.manageIcon}>{t.icon}</span>
                  <span style={S.manageLabel}>{t.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {view === "plans" && (
          <div style={S.wrap}>
            <div style={S.sectionTitle}>Plans to approve</div>
            {plansLoading ? (
              <div style={S.empty}>Loading…</div>
            ) : plansToApprove.length === 0 ? (
              <div style={S.empty}>Nothing waiting on you right now.</div>
            ) : plansToApprove.map(p => (
              <button key={p.id} style={S.listRow} onClick={() => onOpenSermon?.(p.sermons)}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={S.rowLabel}>{p.sermons?.title || "Untitled sermon"}</div>
                  <div style={S.rowSub}>{p.status === "draft" ? "Draft — needs review" : "Approved — ready to publish"}</div>
                </div>
                <div style={S.chevron}>›</div>
              </button>
            ))}
          </div>
        )}

        {view === "access" && (
          <div style={S.wrap}>
            <div style={S.sectionTitle}>Access requests</div>
            {pendingAccess.length === 0 ? (
              <div style={S.empty}>No one's waiting to join right now.</div>
            ) : pendingAccess.map(req => {
              const raw = accessActionState?.[req.id];
              const kind = typeof raw === "string" ? raw : raw?.kind;
              const name = req.profiles?.full_name || "Someone";
              return (
                <div key={req.id} style={S.accessRow}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={S.rowLabel}>{name}</div>
                    {req.message && <div style={S.rowSub}>{req.message}</div>}
                  </div>
                  <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                    <button
                      disabled={kind === "pending" || kind === "done"}
                      onClick={() => onApproveAccess?.(req)}
                      style={{ ...S.approveBtn, opacity: kind === "pending" ? 0.5 : 1, cursor: kind === "pending" ? "default" : "pointer" }}
                    >{kind === "done" ? "Approved" : kind === "pending" ? "…" : "Approve"}</button>
                    <button
                      disabled={kind === "pending" || kind === "done"}
                      onClick={() => onDeclineAccess?.(req)}
                      style={{ ...S.declineBtn, opacity: kind === "pending" ? 0.5 : 1, cursor: kind === "pending" ? "default" : "pointer" }}
                    >Decline</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {view === "leaderPrayers" && (
          <div style={S.wrap}>
            <div style={S.sectionTitle}>Prayer requests for leaders</div>
            <div style={{ fontSize: 12, color: "var(--stone)", marginBottom: 14, lineHeight: 1.4 }}>
              Shared with leaders only — these never appear in Community.
            </div>
            {leaderPrayers.length === 0 ? (
              <div style={S.empty}>Nothing shared with leaders right now.</div>
            ) : leaderPrayers.map(p => (
              <div key={p.id} style={S.accessRow}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={S.rowLabel}>{p.profiles?.full_name || "Someone"}</div>
                  <div style={S.rowSub}>{p.text}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {view === "notices" && <NoticesView churchId={churchId}/>}

        {view === "invites" && (
          <div style={S.wrap}>
            {joinUrl && (
              <div style={S.joinLinkCard}>
                <div style={S.sectionTitle}>Share your join link</div>
                <div style={S.joinLinkSub}>Anyone with this link (or who scans the code) can join your church directly — no per-person invite needed.</div>
                <div style={S.joinLinkRow}>
                  {qrDataUrl && <img src={qrDataUrl} alt="QR code to join your church" style={S.qrImg}/>}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={S.joinLinkText}>{joinUrl}</div>
                    <button style={S.copyBtn} onClick={copyJoinLink}>{linkCopied ? "Copied ✓" : "Copy link"}</button>
                  </div>
                </div>
              </div>
            )}
            <InvitePanel/>
          </div>
        )}
      </div>

      {insightsOpen && (
        <div style={{ position: "fixed", inset: 0, zIndex: 500, background: "var(--cream)", overflowY: "auto" }}>
          <div style={{ paddingTop: "calc(env(safe-area-inset-top) + 16px)" }}>
            <BibleMapView
              followedIds={new Set([churchId])} homeChurchId={churchId}
              churchById={new Map(churches.map(c => [c.id, c]))}
              user={user} isModerator={isModerator}
              sermonNoteChurchIds={sermonNoteChurchIds}
              initialTab="insights"
              onClose={() => setInsightsOpen(false)}
              onOpenVerse={() => {}}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// Simple pinned-notices manager — list + add/edit/pin, wired directly to
// church_notices (useNotices only reads; writes go straight through here).
function NoticesView({ churchId }) {
  const { notices, loading, refresh } = useNotices(churchId);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  async function addNotice() {
    if (!text.trim() || !churchId) return;
    setSaving(true);
    const { error } = await supabase.from("church_notices").insert({ church_id: churchId, text: text.trim() });
    if (!error) { setText(""); refresh(); }
    setSaving(false);
  }
  async function removeNotice(id) {
    await supabase.from("church_notices").delete().eq("id", id);
    refresh();
  }

  return (
    <div style={S.wrap}>
      <div style={S.sectionTitle}>Pinned notices</div>
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <input value={text} onChange={e => setText(e.target.value)} placeholder="Add a notice…"
          style={{ flex: 1, background: "var(--white)", border: "1px solid var(--border-strong)", borderRadius: 10, padding: "10px 12px", fontSize: 14, fontFamily: SANS, outline: "none" }}/>
        <button onClick={addNotice} disabled={saving || !text.trim()} style={{ ...S.approveBtn, opacity: saving || !text.trim() ? 0.5 : 1 }}>Add</button>
      </div>
      {loading ? (
        <div style={S.empty}>Loading…</div>
      ) : notices.length === 0 ? (
        <div style={S.empty}>No notices pinned.</div>
      ) : notices.map(n => (
        <div key={n.id} style={S.accessRow}>
          <div style={{ flex: 1, minWidth: 0 }}><div style={S.rowLabel}>{n.text}</div></div>
          <button onClick={() => removeNotice(n.id)} style={S.declineBtn}>Remove</button>
        </div>
      ))}
    </div>
  );
}

const S = {
  overlay: { position: "fixed", inset: 0, zIndex: 500, background: "var(--cream)", display: "flex", flexDirection: "column" },
  header: { flexShrink: 0, display: "flex", alignItems: "center", gap: 12, padding: "16px 16px 10px", borderBottom: "1px solid var(--border)" },
  backCircle: { flexShrink: 0, width: 44, height: 44, borderRadius: 22, border: "none", background: "var(--white)", color: "var(--ink)", fontSize: 18, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" },
  churchName: { fontSize: 12.5, color: "var(--stone)", fontFamily: SANS },
  pageTitle: { fontSize: 26, fontWeight: 800, color: "var(--ink)", letterSpacing: "-0.02em", fontFamily: SANS },
  closeBtn: { background: "rgba(31,27,22,0.06)", border: "none", color: "var(--stone)", width: 34, height: 34, borderRadius: 999, cursor: "pointer", fontSize: 15, display: "flex", alignItems: "center", justifyContent: "center" },
  backBtn: { background: "transparent", border: "none", color: "var(--ink)", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: SANS, padding: "6px 0", flex: 1 },

  wrap: { padding: "16px 16px calc(env(safe-area-inset-bottom) + 24px)", fontFamily: SANS, maxWidth: 560, margin: "0 auto" },
  empty: { fontSize: 13.5, color: "var(--stone)", padding: "30px 4px", textAlign: "center" },
  sectionTitle: { fontSize: 11.5, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--gold-ink)", marginBottom: 10 },

  card: { background: "var(--white)", borderRadius: 22, padding: 14, marginBottom: 14 },
  cardHeadRow: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 },
  cardLabel: { fontSize: 12, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--gold-ink)", margin: "0 0 10px" },
  caughtUp: { fontSize: 13.5, color: "var(--stone)", padding: "8px 0" },

  attentionRow: { width: "100%", display: "flex", alignItems: "center", gap: 10, background: "none", border: "none", padding: "8px 0", cursor: "pointer", fontFamily: SANS, textAlign: "left", borderTop: "1px solid var(--border)" },
  attentionIcon: { flexShrink: 0, width: 38, height: 38, borderRadius: 19, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 },
  createItBtn: { flexShrink: 0, background: "#1F1B16", color: "#FFFFFF", fontSize: 12, fontWeight: 700, borderRadius: 999, padding: "8px 14px" },
  reviewLink: { flexShrink: 0, color: "var(--gold-ink)", fontSize: 12.5, fontWeight: 700 },

  statsRow: { display: "flex", justifyContent: "space-between" },
  statCol: { flex: 1, textAlign: "center" },
  statValue: { fontSize: 20, fontWeight: 800, color: "var(--ink)" },
  statLabel: { fontSize: 11.5, color: "var(--stone)", marginTop: 2 },

  insightsCard: { width: "100%", display: "flex", alignItems: "center", gap: 10, background: "var(--white)", border: "none", borderRadius: 22, padding: 14, marginBottom: 18, cursor: "pointer", fontFamily: SANS, textAlign: "left" },

  manageGrid: { display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 8 },
  manageTile: { background: "var(--white)", border: "none", borderRadius: 18, padding: "14px 8px", display: "flex", flexDirection: "column", alignItems: "center", gap: 6, cursor: "pointer", fontFamily: SANS },
  manageIcon: { fontSize: 20, color: "var(--gold-ink)" },
  manageLabel: { fontSize: 12.5, fontWeight: 800, color: "var(--ink)", textAlign: "center" },

  row: { width: "100%", display: "flex", alignItems: "center", gap: 10, background: "var(--white)", border: "1px solid var(--border)", borderRadius: 16, padding: "14px 14px", marginBottom: 10, cursor: "pointer", fontFamily: SANS, textAlign: "left" },
  listRow: { width: "100%", display: "flex", alignItems: "center", gap: 10, background: "var(--white)", border: "1px solid var(--border)", borderRadius: 16, padding: "14px 14px", marginBottom: 10, cursor: "pointer", fontFamily: SANS, textAlign: "left" },
  accessRow: { display: "flex", alignItems: "center", gap: 10, background: "var(--white)", border: "1px solid var(--border)", borderRadius: 16, padding: "14px 14px", marginBottom: 10 },
  rowLabel: { fontSize: 14.5, fontWeight: 700, color: "var(--ink)" },
  rowSub: { fontSize: 12, color: "var(--stone)", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  badge: { flexShrink: 0, background: GOLD, color: "var(--ink)", fontSize: 12, fontWeight: 800, borderRadius: 999, minWidth: 22, height: 22, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 6px" },
  chevron: { flexShrink: 0, fontSize: 18, color: "var(--stone-dim)" },

  approveBtn: { background: GOLD, color: "var(--ink)", border: "none", borderRadius: 8, padding: "8px 12px", fontSize: 12.5, fontWeight: 800, cursor: "pointer", fontFamily: SANS },
  declineBtn: { background: "transparent", border: "1px solid var(--border-strong)", color: "var(--stone)", borderRadius: 8, padding: "8px 12px", fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: SANS },

  joinLinkCard: { background: "var(--white)", border: "1px solid var(--border)", borderRadius: 16, padding: 14, marginBottom: 18 },
  joinLinkSub: { fontSize: 12, color: "var(--stone)", lineHeight: 1.4, marginBottom: 12 },
  joinLinkRow: { display: "flex", gap: 12, alignItems: "center" },
  qrImg: { flexShrink: 0, width: 80, height: 80, borderRadius: 8, border: "1px solid var(--border)" },
  joinLinkText: { fontSize: 12, color: "var(--ink)", wordBreak: "break-all", marginBottom: 8 },
  copyBtn: { background: "transparent", border: "1px solid var(--border-strong)", color: "var(--ink)", borderRadius: 8, padding: "7px 12px", fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: SANS },
};
